import { useMessagesRepository } from '@Hooks/useRepository';
import { type ApiNotification } from '@Model';
import { NotificationsRepository } from '@Repositories/Messages';
import { type ListInfo, unwrapOr } from '@synqo/frontend-component-library';
import { useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';

const PAGE_SIZE = 20;

const emptyPage: ListInfo<ApiNotification> = {
    items: [],
    total_item_count: 0,
    page_count: 0,
    page_no: 0,
    page_size: 0,
};

export function useNotificationsQuery(onlyUnread: boolean) {
    const notificationsApi = useMessagesRepository(NotificationsRepository);
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => ['notifications', 'list', { onlyUnread }], [onlyUnread]);

    const query = useInfiniteQuery<ListInfo<ApiNotification>>({
        queryKey,
        initialPageParam: 0,
        queryFn: async ({ pageParam }) => {
            const result = await notificationsApi.query(Number(pageParam), PAGE_SIZE, onlyUnread);
            return unwrapOr(result, emptyPage);
        },
        getNextPageParam: (lastPage) => {
            const nextPage = lastPage.page_no + 1;
            return nextPage < lastPage.page_count ? nextPage : undefined;
        },
        refetchOnWindowFocus: false,
    });

    const notifications = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);

    return {
        notifications,
        isLoading: query.isLoading,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        fetchNextPage: query.fetchNextPage,
        resetToFirstPage: useCallback(() => queryClient.resetQueries({ queryKey }), [queryClient, queryKey]),
    };
}
